import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import Footer from "../../../core/common/footer/Footer";
import { all_routes } from "../../router/all_routes";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { apibaseurl } from "../../../environment";
import useApi from "../../../core/customhook/useApi";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import z from "zod";
import Swal from "sweetalert2";
import { triggerPackageReload } from "../../../core/data/redux/packageSlice";
import { useAuth } from "../../auth/AuthContext";
import { disabledStatuses } from "../../../core/common/DateValidHelper";

const SecondEducationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { authData } = useAuth();

  const {
    assignment_template_id,
    assignment_id,
    case_id,
    candidate_id,
    actor_type,
  } = state || {};
  const qualificationParam = location?.state?.qualification;

  const templates = useSelector((state: RootState) => state.package.templates);

  const educationTemplate = templates.find(
    (item: any) => item.check_name === "Highest Education",
  );

  const assignmentTemplateId = educationTemplate?.assignment_template_id;
  const caseId = educationTemplate?.case_id;
  const assignmentId = educationTemplate?.assignment_id;
  const candidateId = authData?.candidate?.candidateId;

  const templateStatus = educationTemplate?.status;

  const [uploadType, setUploadType] = useState("scan");
  const [formFields, setFormFields] = useState<any[]>([]);
  const [educationType, setEducationType] = useState("");
  const [formData, setFormData] = useState<any>({});
  const [noDocument, setNoDocument] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // ======= Univerty api call  State =============

  const [universitySearch, setUniversitySearch] = useState<{
    fieldId: string;
    value: string;
  } | null>(null);

  const [universityOptions, setUniversityOptions] = useState<any[]>([]);

  const [activeUniversityField, setActiveUniversityField] = useState<
    string | null
  >(null);

  // ======= Modal State =============

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyDocuments, setReplyDocuments] = useState<File[]>([]);

  // ======== Modal stat eend her e===============
  // =========m Validation ==========

  const generateSchema = (
    fields: any[],
    noDocument: Record<string, boolean>,
  ) => {
    const schemaFields: Record<string, any> = {};

    fields.forEach((field) => {
      let validator: any = z.any();

      if (field.type !== "file") {
        validator = z.string();

        if (field.required) {
          validator = validator.min(1, `${field.label} is required`);
        }

        if (field?.required && field.validation_rules?.minLength) {
          validator = validator.min(
            field.validation_rules.minLength,
            `${field.label} must be at least ${field.validation_rules.minLength} characters`,
          );
        }

        if (field?.required && field.validation_rules?.maxLength) {
          validator = validator.max(
            field.validation_rules.maxLength,
            `${field.label} cannot exceed ${field.validation_rules.maxLength} characters`,
          );
        }

        if (field?.required && field.validation_rules?.regex) {
          validator = validator.regex(
            new RegExp(field.validation_rules.regex),
            `${field.label} is invalid`,
          );
        }
      }

      if (field.type === "file") {
        const skipValidation = noDocument?.[field.field_id] === true;

        // Check documents already uploaded through API
        const hasExistingDocument =
          Array.isArray(field.uploaded_files) &&
          field.uploaded_files.some((file: any) => Boolean(file?.url));

        // Existing document or noDocument:
        // No need to upload the same document again.
        if (skipValidation || hasExistingDocument) {
          validator = z.any().optional().nullable();
        } else if (field.file_config?.multiple) {
          // Multiple file upload validation
          validator = field.required
            ? z.array(z.instanceof(File)).min(1, `${field.label} is required`)
            : z.array(z.instanceof(File)).optional().nullable();
        } else {
          // Single file upload validation
          validator = field.required
            ? z.instanceof(File, {
                message: `${field.label} is required`,
              })
            : z.any().optional().nullable();
        }
      }

      schemaFields[field.field_id] = validator;
    });

    return z.object(schemaFields);
  };

  const formFieldsAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}?education_type=${encodeURIComponent(qualificationParam)}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateSaveAPI = useApi<any>(
    `/api/v1/candidate/templates/${assignmentTemplateId}/save`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const templateUpdateAPI = useApi<any>(
    `/api/v1/candidate/forms/filled/${assignmentTemplateId}`,
    {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const insuffResponse = useApi<any>(
    `/api/v1/insufficiency/candidate-response`,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const educationForm = async () => {
    try {
      const response = await formFieldsAPI.callApi();

      if (response?.success) {
        const fields = response?.data?.fields || [];
        const education_type = response?.data?.education_type;

        setEducationType(education_type);
        setFormFields(fields);

        const initialValues: any = {};

        // Initialize all form fields
        fields.forEach((field: any) => {
          initialValues[field.field_id] =
            field.type === "file" ? null : (field.value ?? "");
        });

        // Get user personal data
        const userPersonalData = JSON.parse(
          localStorage.getItem("userPersonalData") || "{}",
        );

        // Create full name
        const fullName = [
          userPersonalData?.first_name,
          userPersonalData?.middle_name,
          userPersonalData?.last_name,
        ]
          .filter(Boolean)
          .join(" ");

        // Education type -> Applicant Name field
        const applicantNameFieldMap: Record<string, string> = {
          post_graduation: "post_graduation_applicant_name",
          graduation: "graduation_applicant_name",
          diploma_professional_certification: "diploma_applicant_name",
          "12th_standard": "12th_standard_applicant_name",
          "10th_standard": "10th_standard_applicant_name",
        };

        const applicantNameField = applicantNameFieldMap[education_type];

        // Automatically show full name in Applicant Name input
        if (
          fullName &&
          applicantNameField &&
          applicantNameField in initialValues
        ) {
          // Only set full name if API did not already provide a value
          if (!initialValues[applicantNameField]) {
            initialValues[applicantNameField] = fullName;
          }
        }

        setFormData(initialValues);
      }
    } catch (error) {
      console.error("Error fetching form fields:", error);
    }
  };
  const formLoading = formFieldsAPI.loading;

  useEffect(() => {
    if (assignmentTemplateId) {
      educationForm();
    }
  }, [assignmentTemplateId]);

  const universityFieldIds = [
    "post_graduation_university_board_name",
    "post_graduation_college_name",
    "graduation_university_board_name",
    "graduation_college_name",
    "diploma_university_board_name",
    "diploma_college_name",
  ];

  const callUniversityAPI = async (value: string, fieldId: string) => {
    try {
      const response = await fetch(
        // `http://universities.hipolabs.com/search?name=${encodeURIComponent(
        `https://candidate.auptech.in/api/universities?name=${encodeURIComponent(
          value,
        )}&country=India`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch universities");
      }

      const data = await response.json();

      setUniversityOptions(data);
      setActiveUniversityField(fieldId);
    } catch (error) {
      console.error("University API Error:", error);

      setUniversityOptions([]);
    }
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldId]: value,
    }));

    if (universityFieldIds.includes(fieldId)) {
      setActiveUniversityField(fieldId);

      setUniversitySearch({
        fieldId,
        value,
      });
    } else {
      setActiveUniversityField(null);
      setUniversityOptions([]);
    }
  };

  useEffect(() => {
    if (!universitySearch?.value?.trim()) {
      setUniversityOptions([]);
      return;
    }

    const timer = setTimeout(() => {
      callUniversityAPI(universitySearch.value, universitySearch.fieldId);
    }, 1000);

    return () => clearTimeout(timer);
  }, [universitySearch]);

  const handleUniversitySelect = (fieldId: string, universityName: string) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldId]: universityName,
    }));

    setUniversityOptions([]);
    setActiveUniversityField(null);
    setUniversitySearch(null);

    setErrors((prev) => ({
      ...prev,
      [fieldId]: "",
    }));
  };

  const handleNoDocumentChange = (fieldId: string, checked: boolean) => {
    setNoDocument((prev) => ({
      ...prev,
      [fieldId]: checked,
    }));

    if (checked) {
      handleFieldChange(fieldId, null);

      // Clear the file input so filename disappears
      if (fileInputRefs.current[fieldId]) {
        fileInputRefs.current[fieldId]!.value = "";
      }
    }
  };

  const handleSaveDocument = async () => {
    try {
      // Dynamic Validation
      const schema = generateSchema(formFields, noDocument);

      const validationResult = schema.safeParse(formData);

      if (!validationResult.success) {
        const fieldErrors: Record<string, string> = {};

        validationResult.error.issues.forEach((issue) => {
          const fieldName = issue.path[0] as string;

          if (!fieldErrors[fieldName]) {
            fieldErrors[fieldName] = issue.message;
          }
        });

        setErrors(fieldErrors);
        return;
      }

      setErrors({});

      // window.alert("Pass Validation");
      // return;

      const payload = new FormData();

      const jsonData: any = {
        ...formData,
        education_type: qualificationParam,
      };

      // Object.keys(formData).forEach((key) => {
      //   const value = formData[key];

      //   if (value instanceof File) {
      //     payload.append(key, value);
      //     delete jsonData[key];
      //   }
      // });

      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        if (value instanceof File) {
          payload.append(key, value);
          delete jsonData[key];
        } else if (Array.isArray(value)) {
          value.forEach((file) => {
            if (file instanceof File) {
              payload.append(key, file);
            }
          });

          delete jsonData[key];
        }
      });
      payload.append("formData", JSON.stringify(jsonData));
      payload.append("form_type", qualificationParam);

      const response = await templateSaveAPI.callApi(payload);

      // payload.append("formData", JSON.stringify(jsonData));
      // const response = await templateSaveAPI.callApi(payload);
      // const response = await templateSaveAPI.callApi({
      //   payload,
      //   form_type: qualificationParam,
      // });

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document saved successfully",
          confirmButtonText: "OK",
        });
        dispatch(triggerPackageReload());
        // Call the correct form reload function for this page
        educationForm();
      }
    } catch (error: any) {
      console.error("Save Error:", error);

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: error?.message || "Something went wrong",
      });
    }
  };

  const handleUpdateDocument = async () => {
    try {
      // Dynamic Validation
      const schema = generateSchema(formFields, noDocument);

      const validationResult = schema.safeParse(formData);

      if (!validationResult.success) {
        const fieldErrors: Record<string, string> = {};

        validationResult.error.issues.forEach((issue) => {
          const fieldName = issue.path[0] as string;

          if (!fieldErrors[fieldName]) {
            fieldErrors[fieldName] = issue.message;
          }
        });

        setErrors(fieldErrors);
        return;
      }

      setErrors({});

      const payload = new FormData();

      const jsonData: any = {
        ...formData,
        education_type: qualificationParam,
      };

      Object.keys(formData).forEach((key) => {
        const value = formData[key];

        if (value instanceof File) {
          payload.append(key, value);
          delete jsonData[key];
        }
      });

      payload.append("formData", JSON.stringify(jsonData));
      payload.append("form_type", qualificationParam);

      const response = await templateUpdateAPI.callApi(payload);

      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Document update successfully",
          confirmButtonText: "OK",
        });
        // navigate(all_routes.caseSummary);
        // dispatch(triggerPackageReload());
        // educationForm();
      }
    } catch (error: any) {
      console.error("Update Error:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error?.message || "Something went wrong",
      });
    }
  };

  const handleReplyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    setReplyDocuments(Array.from(e.target.files));
  };

  const removeReplyFile = (index: number) => {
    setReplyDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitReply = async () => {
    if (!replyRemarks.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Remarks Required",
        text: "Please enter your remarks before submitting.",
      });
      return;
    }

    try {
      const payload = new FormData();

      payload.append(
        "assignment_template_id",
        assignment_template_id ?? assignmentTemplateId ?? "",
      );

      payload.append("assignment_id", assignment_id ?? assignmentId ?? "");

      payload.append("case_id", case_id ?? caseId ?? "");

      payload.append("candidate_id", candidate_id ?? candidateId ?? "");

      payload.append("actor_due_on", actor_type ?? "CANDIDATE");
      payload.append("remarks", replyRemarks);

      // Multiple documents
      replyDocuments.forEach((file) => {
        payload.append("documents", file);
      });

      const res = await insuffResponse.callApi(payload);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Your response has been submitted successfully.",
        });

        // Reset form
        // setReplyRemarks("");
        // setReplyDocuments([]);
        // setShowReplyModal(false);
        navigate(all_routes.caseSummary);
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.message || "Unable to submit your response.",
      });
    }
  };


  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-lg-8">
              {formLoading ? (
                <SnipperLoader />
              ) : (
                <>
                  {/* Main Form */}
                  <div className="card overflow-hidden">
                    {/* Header not optn this div */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom px-4 py-3 bg-white">
                      <h5 className="fw-semibold mb-0">
                        {educationType === "post_graduation"
                          ? "Post Graduation/Master degree"
                          : educationType === "graduation"
                            ? "Bachelor's Degree"
                            : educationType ===
                                "diploma_professional_certification"
                              ? "Diploma/Professional Certification"
                              : educationType === "12th_standard"
                                ? "12th Standard"
                                : educationType === "10th_standard"
                                  ? "10th Standard"
                                  : "Education Qualification"}
                      </h5>

                      {templateStatus === "CANDIDATE_SUBMITTED" ? null : (
                        <button
                          className="btn btn-primary"
                          onClick={() =>
                            navigate(
                              `${all_routes.superAdminDashboard}?module=firstEducationForm`,
                            )
                          }
                        >
                          Back
                        </button>
                      )}
                    </div>
                    {/* Header not optn this div */}

                    <div className="p-4">
                      <div className="border rounded-3 p-4 bg-light">
                        {/* Form Fields */}
                        <div className="row">
                          {formFields
                            .filter((field) => field.type !== "file")
                            .sort((a, b) => a.display_order - b.display_order)
                            .map((field) => (
                              <div key={field.field_uuid} className="col-md-6">
                                <label className="form-label fw-medium">
                                  {field.label}

                                  {field.required && (
                                    <span className="text-danger ms-1">*</span>
                                  )}
                                </label>

                                {field.type === "dropdown" ? (
                                  <select
                                    className={`form-select ${
                                      errors[field.field_id] ? "is-invalid" : ""
                                    }`}
                                    value={formData[field.field_id] || ""}
                                    required={field.required}
                                    disabled={disabledStatuses.includes(
                                      templateStatus ?? "",
                                    )}
                                    onChange={(e) => {
                                      const value = e.target.value;

                                      handleFieldChange(field.field_id, value);

                                      setErrors((prev) => ({
                                        ...prev,
                                        [field.field_id]: "",
                                      }));
                                    }}
                                  >
                                    <option value="">
                                      {field.placeholder || "Select"}
                                    </option>

                                    {(field.options || []).map(
                                      (option: any, index: number) => {
                                        if (typeof option === "string") {
                                          return (
                                            <option
                                              key={`${option}-${index}`}
                                              value={option}
                                            >
                                              {option}
                                            </option>
                                          );
                                        }

                                        return (
                                          <option
                                            key={option.value ?? index}
                                            value={option.value ?? ""}
                                          >
                                            {option.label ??
                                              option.key ??
                                              option.value}
                                          </option>
                                        );
                                      },
                                    )}
                                  </select>
                                ) : (
                                  <div className="position-relative">
                                    <input
                                      type={field.type || "text"}
                                      className={`form-control ${
                                        errors[field.field_id]
                                          ? "is-invalid"
                                          : ""
                                      }`}
                                      readOnly={
                                        field.validation_rules?.readonly ===
                                        true
                                      }
                                      placeholder={field.placeholder}
                                      value={
                                        formData[field.field_id] ||
                                        field.validation_rules?.default ||
                                        ""
                                      }
                                      required={field.required}
                                      onChange={(e) => {
                                        handleFieldChange(
                                          field.field_id,
                                          e.target.value,
                                        );

                                        setErrors((prev) => ({
                                          ...prev,
                                          [field.field_id]: "",
                                        }));
                                      }}
                                      disabled={disabledStatuses.includes(
                                        templateStatus ?? "",
                                      )}
                                    />

                                    {/* University suggestions */}
                                    {universityFieldIds.includes(
                                      field.field_id,
                                    ) &&
                                      activeUniversityField ===
                                        field.field_id &&
                                      universityOptions.length > 0 && (
                                        <div
                                          className="position-absolute w-100 bg-white border rounded shadow-sm"
                                          style={{
                                            top: "100%",
                                            left: 0,
                                            zIndex: 1050,
                                            maxHeight: "250px",
                                            overflowY: "auto",
                                          }}
                                        >
                                          {universityOptions.map(
                                            (
                                              university: any,
                                              index: number,
                                            ) => (
                                              <div
                                                key={`${university.name}-${index}`}
                                                className="px-3 py-2 text-dark"
                                                style={{
                                                  cursor: "pointer",
                                                }}
                                                onMouseDown={(e) => {
                                                  e.preventDefault();

                                                  handleUniversitySelect(
                                                    field.field_id,
                                                    university.name,
                                                  );
                                                }}
                                              >
                                                {university.name}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                  </div>
                                )}

                                {errors[field.field_id] && (
                                  <div className="invalid-feedback d-block">
                                    {errors[field.field_id]}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>

                        {/* Documents Section */}
                        <div className="border rounded-4 mt-5 p-4 bg-white shadow-sm">
                          <h5 className="fw-semibold mb-0">
                            {educationType === "post_graduation"
                              ? "Post Graduation/Master degree Documents"
                              : educationType === "graduation"
                                ? "Bachelor's degree Documents"
                                : educationType ===
                                    "diploma_professional_certification"
                                  ? "Diploma/Professional Certification Documents"
                                  : educationType === "12th_standard"
                                    ? "12th Standard Documents"
                                    : educationType === "10th_standard"
                                      ? "10th Standard Documents"
                                      : "Education Qualification"}
                          </h5>

                          {/* Instructions */}
                          <div className="mb-4">
                            <label className="form-label fw-semibold">
                              Instructions:
                            </label>
                            <p>Please fill the form first</p>

                            <div className="border rounded-3 p-3 bg-white mb-4">
                              <div className="d-flex flex-wrap gap-4">
                                {/* DigiLocker */}
                                {/* <div className="form-check">
                                  <input
                                    type="radio"
                                    className="form-check-input"
                                    name="uploadType"
                                    id="digilocker"
                                    checked={uploadType === "digilocker"}
                                    onChange={() => setUploadType("digilocker")}
                                  />

                                  <label
                                    className="form-check-label fw-medium"
                                    htmlFor="digilocker"
                                  >
                                    Upload Document (Digilocker)
                                  </label>
                                </div> */}

                                {/* Scan Copy */}
                                <div className="form-check">
                                  <input
                                    type="radio"
                                    className="form-check-input"
                                    name="uploadType"
                                    id="scanCopy"
                                    checked={uploadType === "scan"}
                                    onChange={() => setUploadType("scan")}
                                  />

                                  <label
                                    className="form-check-label fw-medium"
                                    htmlFor="scanCopy"
                                  >
                                    Upload Document (Scan Copy)
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* {uploadType === "digilocker" && (
                            <div className="border rounded-4 p-4 bg-light mb-4">
                              <h5 className="fw-bold mb-3">Instructions</h5>

                              <ul
                                className="mb-4"
                                style={{ listStyle: "number" }}
                              >
                                <li>
                                  For education verification we will need your
                                  Education Documents issued on DigiLocker
                                </li>

                                <li>
                                  "DigiLocker" is a centralise repository of
                                  digital documents Which is operated by
                                  Government of India
                                </li>

                                <li>
                                  Over 600 universities data is now contained in
                                  repository and many are being added daily
                                </li>
                                <li>
                                  Please ignore if your university or college is
                                  not yet avaliable in DigiLocker or certificate
                                  is not able in Digilocker Universities/College
                                  are still in process of updating their records
                                  in DigiLocker,We will continue to verify your
                                  education background verification through
                                  other sources.{" "}
                                </li>
                              </ul>

                              <h6 className="fw-semibold mb-3">
                                To share the documents follow the below steps
                              </h6>

                              <div className="">
                                <div className="mb-4">
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <div
                                      className="bg-black text-white fw-bold"
                                      style={{
                                        padding: "5px 14px",
                                        borderRadius: "20px",
                                        fontSize: "14px",
                                      }}
                                    >
                                      Step 1
                                    </div>

                                    <p
                                      className="fw-bold mb-0"
                                      style={{ fontSize: "16px" }}
                                    >
                                      Setup your DigiLocker account.
                                    </p>
                                  </div>

                                  <p
                                    className="mb-2"
                                    style={{
                                      fontSize: "14px",
                                    }}
                                  >
                                    Do you have a DigiLocker account?
                                  </p>

                                  <div className="ms-1">
                                    <div className="form-check mb-1">
                                      <input
                                        type="radio"
                                        className="form-check-input"
                                        name="step1"
                                        checked={step1Answer === "yes"}
                                        onChange={() => setStep1Answer("yes")}
                                      />

                                      <label
                                        className="form-check-label fw-semibold"
                                        style={{ fontSize: "14px" }}
                                      >
                                        Yes
                                      </label>
                                    </div>

                                    <div className="form-check">
                                      <input
                                        type="radio"
                                        className="form-check-input"
                                        name="step1"
                                        checked={step1Answer === "no"}
                                        onChange={() => {
                                          setStep1Answer("no");
                                          setStep2Answer("");
                                        }}
                                      />

                                      <label
                                        className="form-check-label fw-semibold"
                                        style={{ fontSize: "14px" }}
                                      >
                                        No
                                      </label>
                                    </div>
                                  </div>
                                </div>
                                {step1Answer === "yes" && (
                                  <div className="mb-4">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <div
                                        className="bg-black text-white fw-bold"
                                        style={{
                                          padding: "5px 14px",
                                          borderRadius: "20px",
                                          fontSize: "14px",
                                        }}
                                      >
                                        Step 2
                                      </div>

                                      <p
                                        className="fw-bold mb-0"
                                        style={{ fontSize: "16px" }}
                                      >
                                        Claim your documents on DigiLocker.
                                      </p>
                                    </div>

                                    <div className="row">
                                      <div className="col-md-8">
                                        <div className="form-check mb-1">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            name="step2"
                                            checked={step2Answer === "yes"}
                                            onChange={() =>
                                              setStep2Answer("yes")
                                            }
                                          />

                                          <label
                                            className="form-check-label fw-semibold"
                                            style={{
                                              fontSize: "14px",
                                              lineHeight: "24px",
                                            }}
                                          >
                                            Yes - Already linked my educational
                                            documents in DigiLocker
                                          </label>
                                        </div>

                                        <div className="form-check mb-1">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            name="step2"
                                            checked={step2Answer === "no"}
                                            onChange={() =>
                                              setStep2Answer("no")
                                            }
                                          />

                                          <label
                                            className="form-check-label fw-semibold"
                                            style={{
                                              fontSize: "14px",
                                              lineHeight: "24px",
                                            }}
                                          >
                                            No - Document is not linked in
                                            DigiLocker
                                          </label>
                                        </div>

                                        <p
                                          className="mb-0 mt-1"
                                          style={{
                                            fontSize: "13px",
                                          }}
                                        >
                                          <span
                                            className="text-decoration-underline"
                                            style={{ cursor: "pointer" }}
                                          >
                                            Click Here
                                          </span>{" "}
                                          for demo on how to claim it
                                        </p>
                                      </div>
                                    </div>

                                    {step2Answer === "no" && (
                                      <div className="mt-3">
                                        <div className="d-flex align-items-start gap-2">
                                          <input
                                            type="checkbox"
                                            style={{
                                              width: "16px",
                                              height: "16px",
                                              marginTop: "3px",
                                            }}
                                          />

                                          <label
                                            style={{
                                              fontSize: "14px",
                                              lineHeight: "24px",
                                            }}
                                          >
                                            I searched for my university/college
                                            in DigiLocker and it is not
                                            available currently.
                                          </label>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {step1Answer === "no" && (
                                  <p
                                    className="mb-0 mt-1"
                                    style={{
                                      fontSize: "13px",
                                    }}
                                  >
                                    <span
                                      className="text-decoration-underline"
                                      style={{ cursor: "pointer" }}
                                    >
                                      Click Here
                                    </span>{" "}
                                    for instraction to setup your account.
                                  </p>
                                )}

                                {step2Answer === "yes" && (
                                  <div>
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                      <div
                                        className="bg-black text-white fw-bold"
                                        style={{
                                          padding: "5px 14px",
                                          borderRadius: "20px",
                                          fontSize: "14px",
                                        }}
                                      >
                                        Step 3
                                      </div>

                                      <p
                                        className="fw-bold mb-0"
                                        style={{ fontSize: "16px" }}
                                      >
                                        Share your document.
                                      </p>
                                    </div>

                                    <p
                                      className="mb-1"
                                      style={{
                                        fontSize: "14px",
                                        lineHeight: "24px",
                                      }}
                                    >
                                      Share documents with Aupertz Services Private Limited.
                                    </p>

                                    <p
                                      className="mb-3"
                                      style={{
                                        fontSize: "14px",
                                        lineHeight: "24px",
                                      }}
                                    >
                                      To share through DigiLocker
                                      <span
                                        className="text-decoration-underline"
                                        style={{ cursor: "pointer" }}
                                      >
                                        {" "}
                                        Click Here{" "}
                                      </span>
                                      for demo instructions.
                                    </p>

                                    <div className="text-center mb-3">
                                      <button
                                        className="btn btn-danger"
                                        style={{
                                          fontSize: "14px",
                                          fontWeight: "600",
                                          padding: "8px 18px",
                                          borderRadius: "5px",
                                          border: "none",
                                        }}
                                      >
                                        Fetch Document From DigiLocker
                                      </button>
                                    </div>

                                    <p
                                      className="text-center mb-2"
                                      style={{
                                        fontSize: "13px",
                                      }}
                                    >
                                      Click below after document submission
                                    </p>

                                    <button
                                      className="btn w-100 text-white"
                                      style={{
                                        backgroundColor: "#df3143",
                                        fontSize: "15px",
                                        fontWeight: "600",
                                        padding: "10px",
                                        borderRadius: "5px",
                                        border: "none",
                                      }}
                                    >
                                      Complete DigiLocker
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )} */}

                          {uploadType === "scan" && (
                            <>
                              <ul
                                className="ps-3 mb-4 text-dark"
                                style={{ listStyle: "number" }}
                              >
                                <li className="mb-2">
                                  Kindly ensure the photocopy/picture is
                                  readable, and is not cut/pixelated/blurred.
                                </li>

                                <li className="mb-2">
                                  Documents Accepted: Pdf, Jpeg, Jpg, Png are
                                  allowed
                                </li>

                                <li>Documents should be less than 10MB</li>
                              </ul>
                              {formFields
                                .filter((field) => field.type === "file")
                                .sort(
                                  (a, b) => a.display_order - b.display_order,
                                )
                                .map((field) => (
                                  <div
                                    key={field.field_uuid}
                                    className="row align-items-center mb-5"
                                  >
                                    <div className="col-lg-8">
                                      {/* <label className="form-label fw-semibold">
                                        {field.label}
                                      </label>

                                      {field.required && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )} */}
                                      <label className="form-label fw-semibold">
                                        {field.label}
                                        {field.required && (
                                          <span className="text-danger ms-1">
                                            *
                                          </span>
                                        )}

                                        {field.file_config?.multiple && (
                                          <span className="text-muted ms-2 fw-normal">
                                            (Select multiple documents)
                                          </span>
                                        )}
                                      </label>
                                      {/* <input
                                        ref={(el) => {
                                          fileInputRefs.current[
                                            field.field_id
                                          ] = el;
                                        }}
                                        type={field.type}
                                        className="form-control"
                                        // accept={field.file_config?.allowedTypes
                                        //   ?.map((type: string) => `.${type}`)
                                        //   .join(",")}
                                        accept={field.file_config?.allowedExtensions
                                          ?.map((type: string) => `.${type}`)
                                          .join(",")}
                                        // onChange={(e) =>
                                        //   handleFieldChange(
                                        //     field.field_id,
                                        //     e.target.files?.[0] || null,
                                        //   )
                                        // }
                                        onChange={(e) => {
                                          if (field.file_config?.multiple) {
                                            handleFieldChange(
                                              field.field_id,
                                              Array.from(e.target.files || []),
                                            );
                                          } else {
                                            handleFieldChange(
                                              field.field_id,
                                              e.target.files?.[0] || null,
                                            );
                                          }
                                        }}
                                        disabled={
                                          noDocument[field.field_id] ||
                                          disabledStatuses.includes(
                                            templateStatus ?? "",
                                          )
                                        }
                                        required={field?.required}
                                      /> */}
                                      <input
                                        ref={(el) => {
                                          fileInputRefs.current[
                                            field.field_id
                                          ] = el;
                                        }}
                                        type="file"
                                        className="form-control"
                                        multiple={field.file_config?.multiple}
                                        accept={field.file_config?.allowedExtensions
                                          ?.map((type: string) => `.${type}`)
                                          .join(",")}
                                        onChange={(e) => {
                                          if (field.file_config?.multiple) {
                                            handleFieldChange(
                                              field.field_id,
                                              Array.from(e.target.files || []),
                                            );
                                          } else {
                                            handleFieldChange(
                                              field.field_id,
                                              e.target.files?.[0] || null,
                                            );
                                          }
                                        }}
                                        disabled={
                                          noDocument[field.field_id] ||
                                          disabledStatuses.includes(
                                            templateStatus ?? "",
                                          )
                                        }
                                        required={
                                          Boolean(field.required) &&
                                          !noDocument?.[field.field_id] &&
                                          !field.uploaded_files?.some(
                                            (file: any) => Boolean(file?.url),
                                          )
                                        }
                                      />
                                      <div className="form-check mt-2">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={
                                            noDocument[field.field_id] || false
                                          }
                                          onChange={(e) =>
                                            handleNoDocumentChange(
                                              field.field_id,
                                              e.target.checked,
                                            )
                                          }
                                        />

                                        <label className="form-check-label text-muted small">
                                          I do not have the document
                                        </label>
                                      </div>
                                      <div className="invalid-feedback d-block">
                                        {errors[field.field_id]}
                                      </div>
                                    </div>

                                    <div className="col-lg-4 text-center mt-4 mt-lg-0">
                                      {field.uploaded_files?.length > 0 ? (
                                        <div className="text-center">
                                          {/* If user selected NEW files, show new files */}
                                          {Array.isArray(
                                            formData[field.field_id],
                                          ) &&
                                          formData[field.field_id].length >
                                            0 ? (
                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "50px",
                                                  cursor: "pointer",
                                                }}
                                              />

                                              {formData[field.field_id].map(
                                                (file: File, index: number) => (
                                                  <p
                                                    key={index}
                                                    className="text-danger small mb-0"
                                                    style={{
                                                      cursor: "pointer",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {file.name}
                                                  </p>
                                                ),
                                              )}
                                            </>
                                          ) : formData[
                                              field.field_id
                                            ] instanceof File ? (
                                            <>
                                              {/* Single newly selected file */}
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "50px",
                                                  cursor: "pointer",
                                                }}
                                              />

                                              <p
                                                className="text-danger small mb-0"
                                                style={{
                                                  cursor: "pointer",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                {formData[field.field_id].name}
                                              </p>
                                            </>
                                          ) : (
                                            <>
                                              {/* Existing uploaded files */}
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{
                                                  fontSize: "50px",
                                                }}
                                              />

                                              {field.uploaded_files.map(
                                                (file: any, index: number) => (
                                                  <a
                                                    key={index}
                                                    href={`${apibaseurl}${file.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none d-block"
                                                  >
                                                    <p
                                                      className="text-danger small mb-0"
                                                      style={{
                                                        cursor: "pointer",
                                                        wordBreak: "break-word",
                                                      }}
                                                    >
                                                      {file.original_name ||
                                                        file.name ||
                                                        `Document ${index + 1}`}
                                                    </p>
                                                  </a>
                                                ),
                                              )}
                                            </>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-center">
                                          {Array.isArray(
                                            formData[field.field_id],
                                          ) &&
                                          formData[field.field_id].length >
                                            0 ? (
                                            <div className="mb-2">
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{ fontSize: "50px" }}
                                              />

                                              {formData[field.field_id].map(
                                                (file: any, index: number) => (
                                                  <p
                                                    key={index}
                                                    className="text-danger small mb-0"
                                                    style={{
                                                      cursor: "pointer",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {file?.name ||
                                                      file?.original_name ||
                                                      "Unknown File"}
                                                  </p>
                                                ),
                                              )}
                                            </div>
                                          ) : formData[field.field_id] ? (
                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{ fontSize: "50px" }}
                                              />

                                              <p
                                                className="text-danger small mb-0"
                                                style={{
                                                  cursor: "pointer",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                {formData[field.field_id]
                                                  ?.name ||
                                                  formData[field.field_id]
                                                    ?.original_name}
                                              </p>
                                            </>
                                          ) : field.uploaded_files?.length >
                                            0 ? (
                                            field.uploaded_files.map(
                                              (file: any, index: number) => (
                                                <div
                                                  key={index}
                                                  className="mb-2"
                                                >
                                                  <i
                                                    className="bi bi-file-earmark-pdf-fill text-danger"
                                                    style={{ fontSize: "50px" }}
                                                  />

                                                  <p
                                                    className="text-danger small mb-0"
                                                    style={{
                                                      cursor: "pointer",
                                                      wordBreak: "break-word",
                                                    }}
                                                  >
                                                    {file?.original_name ||
                                                      file?.name ||
                                                      "Unknown File"}
                                                  </p>
                                                </div>
                                              ),
                                            )
                                          ) : (
                                            <>
                                              <i
                                                className="bi bi-file-earmark-pdf-fill text-danger"
                                                style={{ fontSize: "50px" }}
                                              />
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </>
                          )}
                        </div>

                        {disabledStatuses.includes(templateStatus ?? "") ||
                        templateStatus === "CANDIDATE_INSUFF" ? null : (
                          <div className="d-flex gap-2 align-item-center mt-5">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleSaveDocument}
                              disabled={templateSaveAPI.loading}
                            >
                              {templateSaveAPI.loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <i className="ti ti-device-floppy me-2"></i>
                                  Save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                        {templateStatus === "CANDIDATE_INSUFF" && (
                          <div className="d-flex gap-2 align-items-center mt-5">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleUpdateDocument}
                            >
                              <i className="ti ti-upload me-2"></i>
                              Save Form & Documents
                            </button>

                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={() => setShowReplyModal(true)}
                            >
                              <i className="ti ti-message-circle me-2"></i>
                              Reply to Insufficiency
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="col-md-4">
              <div
                style={{
                  position: "sticky",
                  top: "60px",
                  alignSelf: "flex-start",
                }}
              >
                <div className="card border-0 shadow-sm">
                  {/* Header */}
                  <div
                    className="text-center py-4"
                    style={{ backgroundColor: "#f7f1e3" }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-white"
                      style={{ width: "70px", height: "70px" }}
                    >
                      <i
                        className="bi bi-lightbulb-fill text-warning"
                        style={{ fontSize: "35px" }}
                      ></i>
                    </div>

                    <h6 className="fw-bold mb-0">Instructions</h6>
                  </div>

                  {/* Body */}
                  <div className="card-body">
                    {/* Instruction 1 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>

                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 1</h6>
                        <p className="text-muted mb-0 small">
                          Please provide your relevant education details in the
                          form, and ensure that the relevant document mentions
                          your name exactly as it appears on the official
                          education documents.
                        </p>
                      </div>
                    </div>

                    {/* Instruction 2 */}
                    <div className="d-flex mb-4">
                      <div
                        className="rounded me-3 mt-1 flex-shrink-0"
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: "#f4c542",
                        }}
                      ></div>

                      <div>
                        <h6 className="mb-1 fw-semibold">Instruction 2</h6>

                        <p className="text-muted mb-0 small">
                          In case you changed your name later due to
                          marriage/other reason we have already captured that in
                          the personal informant section.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showReplyModal && (
            <div
              className="modal fade show d-block"
              style={{ background: "rgba(0,0,0,.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                  {/* Header */}
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title fw-bold mb-1">
                        Respond to Insufficiency
                      </h5>

                      <small className="text-muted">
                        Please provide your explanation and upload supporting
                        documents if applicable.
                      </small>
                    </div>

                    <button
                      className="btn-close"
                      onClick={() => setShowReplyModal(false)}
                    />
                  </div>

                  {/* Body */}
                  <div className="modal-body">
                    {/* Remarks */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Remarks
                        <span className="text-danger ms-1">*</span>
                      </label>

                      <textarea
                        rows={5}
                        className="form-control"
                        placeholder="Enter your response to the verifier..."
                        value={replyRemarks}
                        onChange={(e) => setReplyRemarks(e.target.value)}
                        style={{ resize: "none" }}
                      />

                      <small className="text-muted">
                        Clearly explain the requested information or
                        clarification.
                      </small>
                    </div>

                    {/* Upload */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Supporting Documents
                      </label>

                      <input
                        type="file"
                        className="form-control"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleReplyFileChange}
                      />

                      <small className="text-muted">
                        Accepted formats: JPG, JPEG, PNG, PDF (Maximum 10 MB per
                        file)
                      </small>
                    </div>

                    {/* Selected Files */}
                    {replyDocuments.length > 0 && (
                      <div className="mt-3">
                        <label className="fw-semibold mb-2">
                          Selected Files
                        </label>

                        <div className="d-flex flex-wrap gap-2">
                          {replyDocuments.map((file, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill bg-success-subtle text-dark border border-success-subtle px-3 py-2 d-flex align-items-center"
                            >
                              <i className="ti ti-paperclip me-2"></i>

                              <span
                                className="text-truncate"
                                style={{ maxWidth: 180 }}
                              >
                                {file.name}
                              </span>

                              <button
                                type="button"
                                className="btn btn-sm border-0 bg-transparent text-danger ms-2 p-0"
                                onClick={() => removeReplyFile(index)}
                              >
                                <i className="ti ti-x"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="modal-footer gap-3">
                    <button
                      className="btn btn-light border"
                      onClick={() => setShowReplyModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitReply}
                      disabled={insuffResponse.loading}
                    >
                      {insuffResponse.loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-send me-2"></i>
                          Submit Response
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default SecondEducationForm;
